/**
 * The game's DEF tables, built exactly as FWP.init_data_arrays builds them (FWP.as:881-10478): the same data
 * factories of src/game/data, in the same order, with the same post-processing of WEAPONS_DEF. FWP.init_data_arrays
 * itself calls createGameDefs(), so the client and the backend can never diverge.
 *
 * Every call returns fresh copies (the game mutates its tables, e.g. end_game_fn re-runs init_data_arrays to undo
 * battle-time changes). Names inside the tables are the canonical Russian strings of the original (the UI translates
 * them for display), and rules must never branch on a name - use table keys and ids.
 *
 * Imports: only src/game/data/* and ./as3 (see the browser-safety rule in ./index.ts).
 */
import { create_ACHIEV_DEF } from '../data/ACHIEV_DEF';
import { create_ACHIEV_PRISE_DEF } from '../data/ACHIEV_PRISE_DEF';
import { create_ACHIEV_RATING_BONUS } from '../data/ACHIEV_RATING_BONUS';
import { create_BOOKS_DEF } from '../data/BOOKS_DEF';
import { create_COREAN_RANDOM } from '../data/COREAN_RANDOM';
import { create_CRAFT_DEF } from '../data/CRAFT_DEF';
import { create_FLAGS_DEF } from '../data/FLAGS_DEF';
import { create_GUM_DEF } from '../data/GUM_DEF';
import { create_IMPL_DEF } from '../data/IMPL_DEF';
import { create_LEAGUE_THRESHOLD } from '../data/LEAGUE_THRESHOLD';
import { create_LEVEL_THRESHOLD } from '../data/LEVEL_THRESHOLD';
import { create_MISSION_DESC } from '../data/MISSION_DESC';
import { create_MISSION_WEAP_DEF } from '../data/MISSION_WEAP_DEF';
import { create_OBJ_DEF } from '../data/OBJ_DEF';
import { create_PAPA_HOURS } from '../data/PAPA_HOURS';
import { create_PAPA_OBJECTS } from '../data/PAPA_OBJECTS';
import { create_PAPA_WEAPONS } from '../data/PAPA_WEAPONS';
import { create_PEACHFUL_BONUS } from '../data/PEACHFUL_BONUS';
import { create_PERKS_DEF } from '../data/PERKS_DEF';
import { create_RIPS_DEF } from '../data/RIPS_DEF';
import { create_ROUL_VER } from '../data/ROUL_VER';
import { create_SPEC_DEF } from '../data/SPEC_DEF';
import { create_STIM_DEF } from '../data/STIM_DEF';
import { create_TASK_DEF } from '../data/TASK_DEF';
import { create_TEAMS_DEF } from '../data/TEAMS_DEF';
import { create_TIRED_EFFECTS } from '../data/TIRED_EFFECTS';
import { create_WEAPONS_DEF } from '../data/WEAPONS_DEF';
import { create_WEAPON_ICONS } from '../data/WEAPON_ICONS';
import { create_WORLD_DEF } from '../data/WORLD_DEF';
import { create_WP_PLACES_SORT } from '../data/WP_PLACES_SORT';
import { keys } from './as3';

// ------------------------------------------------------------------------------------------------ light entry types
// The tables are untyped literals of the original; these interfaces only document the fields the meta-game rules
// read most. Every entry may carry more fields (index signature), so ported code keeps compiling unchanged.

/** A buyable entry of STIM_DEF / FLAGS_DEF / RIPS_DEF / IMPL_DEF / OBJ_DEF (the shop reads these price fields). */
export interface ShopItemDef {
  id: number;
  gold: number;
  diam: number;
  level: number;
  limit: number;
  /** Canonical (Russian) display name - never branch on it. */
  name: string;
  [field: string]: any;
}

/** A WEAPONS_DEF entry after the init_data_arrays post-processing (see prepareWeaponsDef). */
export interface WeaponDef extends ShopItemDef {
  /** Weapon class 0..8, derived from `level` by init_data_arrays. */
  wpclass: number;
  /** Weapon type letter used by perks and tasks (e.g. 'p', 'h'). */
  wptype: string;
  /** Listed in the shop; hidden (false) weapons with level != 0 cost ceil(price / 0.6). */
  shown: boolean;
}

/** A PERKS_DEF entry. */
export interface PerkDef {
  id: number;
  level: number;
  max_lim: number;
  mode: string;
  /** Skill-point price of each perk level (upg[n] = price of level n + 1). */
  upg: number[];
  name: string;
  [field: string]: any;
}

/** Every table FWP.init_data_arrays assigns, under the FWP field name. */
export interface GameDefs {
  WORLD_DEF: Record<string, any>;
  TIRED_EFFECTS: any[];
  STIM_DEF: Record<string, ShopItemDef>;
  FLAGS_DEF: Record<string, ShopItemDef>;
  RIPS_DEF: Record<string, ShopItemDef>;
  IMPL_DEF: Record<string, ShopItemDef>;
  OBJ_DEF: Record<string, ShopItemDef>;
  BOOKS_DEF: Record<string, any>;
  /** [count, [spec pair 1], ...]. */
  SPEC_DEF: any[];
  PEACHFUL_BONUS: Record<string, any>;
  /** LEVEL_THRESHOLD[l] = exp needed for level l. */
  LEVEL_THRESHOLD: number[];
  LEAGUE_THRESHOLD: number[];
  PERKS_DEF: Record<string, PerkDef>;
  WEAPONS_DEF: Record<string, WeaponDef>;
  GUM_DEF: Record<string, number>;
  MISSION_WEAP_DEF: Record<string, Record<string, number>>;
  /** Stage definitions (career1.., missions1.., survival1.., boss1.., assault1.., just1..) and the *_max counts. */
  TEAMS_DEF: Record<string, any>;
  MISSION_DESC: any;
  ROUL_VER: Record<string, number[]>;
  /** [count, {id, levels, name}, ...]. */
  ACHIEV_DEF: any[];
  ACHIEV_PRISE_DEF: Record<string, { prise: string; amount: number[] }>;
  ACHIEV_RATING_BONUS: any[];
  /** [count, {req, result, ingr: [n, ...ids]}, ...]. */
  CRAFT_DEF: any[];
  /** [count, {bonus_type, bonus, name}, ...]. */
  TASK_DEF: any[];
  COREAN_RANDOM: any[];
  /** PAPA_HOURS[day of month] = the hour Papa John is in town. */
  PAPA_HOURS: number[];
  PAPA_WEAPONS: any[];
  PAPA_OBJECTS: any[];
  WEAPON_ICONS: Record<string, any>;
  /** Sparse: ban reason texts by ban code (101, 102, 103, 201, 202). */
  BAN_DESC: string[];
  WP_PLACES_SORT: any;
}

/** Field names of GameDefs in the order init_data_arrays assigns them. */
export const GAME_DEF_NAMES: readonly (keyof GameDefs)[] = [
  'WORLD_DEF',
  'TIRED_EFFECTS',
  'STIM_DEF',
  'FLAGS_DEF',
  'RIPS_DEF',
  'IMPL_DEF',
  'OBJ_DEF',
  'BOOKS_DEF',
  'SPEC_DEF',
  'PEACHFUL_BONUS',
  'LEVEL_THRESHOLD',
  'LEAGUE_THRESHOLD',
  'PERKS_DEF',
  'WEAPONS_DEF',
  'GUM_DEF',
  'MISSION_WEAP_DEF',
  'TEAMS_DEF',
  'MISSION_DESC',
  'ROUL_VER',
  'ACHIEV_DEF',
  'ACHIEV_PRISE_DEF',
  'ACHIEV_RATING_BONUS',
  'CRAFT_DEF',
  'TASK_DEF',
  'COREAN_RANDOM',
  'PAPA_HOURS',
  'PAPA_WEAPONS',
  'PAPA_OBJECTS',
  'WEAPON_ICONS',
  'BAN_DESC',
  'WP_PLACES_SORT',
];

/**
 * The WEAPONS_DEF post-processing of init_data_arrays (FWP.as:5416-5462), in place: `wpclass` by level (0 for
 * craft-only, 1..7 by level bands, 8 for the three Kenton weapons), hidden weapons that are sold (level != 0) cost
 * ceil(price / 0.6) in gold and diamonds, and three weapons are forced to class 4 afterwards.
 */
export function prepareWeaponsDef(w: Record<string, any>): Record<string, WeaponDef> {
  for (const k of keys(w)) {
    const d = w[k];
    if (d.level == 0) {
      d["wpclass"] = 0;
    } else if (d.level == 1) {
      d["wpclass"] = 1;
    } else if (d.level <= 8) {
      d["wpclass"] = 2;
    } else if (d.level <= 15) {
      d["wpclass"] = 3;
    } else if (d.level <= 20) {
      d["wpclass"] = 4;
    } else if (d.level <= 29) {
      d["wpclass"] = 5;
    } else if (d.level <= 40) {
      d["wpclass"] = 6;
    } else {
      d["wpclass"] = 7;
    }
    if (k == "kenton_bazooka" || k == "kenton_grenade" || k == "void_melody") {
      d["wpclass"] = 8;
    }
    if (!d.shown && d.level != 0) {
      d.gold = Math.ceil(d.gold / 0.6);
      d.diam = Math.ceil(d.diam / 0.6);
    }
  }
  w.holy_hand_grenade.wpclass = 4;
  w.unfail_minigun.wpclass = 4;
  w.mine_strike.wpclass = 4;
  return w as Record<string, WeaponDef>;
}

/** The ban reason texts init_data_arrays writes into FWP.BAN_DESC (a sparse array indexed by ban code). */
export function createBanDesc(): string[] {
  const b: string[] = [];
  b[101] = "Cheating";
  b[102] = "Bad Software Distribution";
  b[103] = "Using Bugs";
  b[201] = "Spam";
  b[202] = "Multiapp Using Attempt";
  return b;
}

/** Fresh copies of every table of FWP.init_data_arrays, built in the same order and post-processed the same way. */
export function createGameDefs(): GameDefs {
  const WORLD_DEF = create_WORLD_DEF();
  const TIRED_EFFECTS = create_TIRED_EFFECTS();
  const STIM_DEF = create_STIM_DEF();
  const FLAGS_DEF = create_FLAGS_DEF();
  const RIPS_DEF = create_RIPS_DEF();
  const IMPL_DEF = create_IMPL_DEF();
  const OBJ_DEF = create_OBJ_DEF();
  const BOOKS_DEF = create_BOOKS_DEF();
  const SPEC_DEF = create_SPEC_DEF();
  const PEACHFUL_BONUS = create_PEACHFUL_BONUS();
  const LEVEL_THRESHOLD = create_LEVEL_THRESHOLD();
  const LEAGUE_THRESHOLD = create_LEAGUE_THRESHOLD();
  const PERKS_DEF = create_PERKS_DEF();
  const WEAPONS_DEF = prepareWeaponsDef(create_WEAPONS_DEF());
  const GUM_DEF = create_GUM_DEF();
  const MISSION_WEAP_DEF = create_MISSION_WEAP_DEF();
  const TEAMS_DEF = create_TEAMS_DEF();
  const MISSION_DESC = create_MISSION_DESC();
  const ROUL_VER = create_ROUL_VER();
  const ACHIEV_DEF = create_ACHIEV_DEF();
  const ACHIEV_PRISE_DEF = create_ACHIEV_PRISE_DEF();
  const ACHIEV_RATING_BONUS = create_ACHIEV_RATING_BONUS();
  const CRAFT_DEF = create_CRAFT_DEF();
  const TASK_DEF = create_TASK_DEF();
  const COREAN_RANDOM = create_COREAN_RANDOM();
  const PAPA_HOURS = create_PAPA_HOURS();
  const PAPA_WEAPONS = create_PAPA_WEAPONS();
  const PAPA_OBJECTS = create_PAPA_OBJECTS();
  const WEAPON_ICONS = create_WEAPON_ICONS();
  const BAN_DESC = createBanDesc();
  const WP_PLACES_SORT = create_WP_PLACES_SORT();
  return {
    WORLD_DEF,
    TIRED_EFFECTS,
    STIM_DEF,
    FLAGS_DEF,
    RIPS_DEF,
    IMPL_DEF,
    OBJ_DEF,
    BOOKS_DEF,
    SPEC_DEF,
    PEACHFUL_BONUS,
    LEVEL_THRESHOLD,
    LEAGUE_THRESHOLD,
    PERKS_DEF,
    WEAPONS_DEF,
    GUM_DEF,
    MISSION_WEAP_DEF,
    TEAMS_DEF,
    MISSION_DESC,
    ROUL_VER,
    ACHIEV_DEF,
    ACHIEV_PRISE_DEF,
    ACHIEV_RATING_BONUS,
    CRAFT_DEF,
    TASK_DEF,
    COREAN_RANDOM,
    PAPA_HOURS,
    PAPA_WEAPONS,
    PAPA_OBJECTS,
    WEAPON_ICONS,
    BAN_DESC,
    WP_PLACES_SORT,
  };
}

/**
 * FWP.get_player_level (FWP.as:18377-18386): the largest level whose LEVEL_THRESHOLD is <= exp. Shared by the ops
 * because many of them recompute the level from exp.
 */
export function playerLevel(defs: Pick<GameDefs, 'LEVEL_THRESHOLD'>, exp: number): number {
  exp = Number(exp);
  let l = 1;
  // Past the end of the table the comparison with undefined is false, as in the original unbounded loop.
  while (defs.LEVEL_THRESHOLD[l] <= exp) l++;
  return l - 1;
}
